/**
 * RatingTrend — the impact history as a small chart: one point per formal
 * review, oldest on the left, on the four-step scale (Weak, Not yet full,
 * Full, Exceptional). Too soon to rate, and any F&O wording that does not fit
 * the scale, sits below the scale as a dashed tick. Filled points are the
 * platform's ratings; hollow ones are F&O's from before the platform. Each
 * point carries a native title with the period and the rating.
 */
import { cn } from '@/lib/utils'
import type { TalentRatingRecord } from '@/services/api/talentReviewService'
import { MIDYEAR_RATING_LABEL, RATING_PERIOD_LABEL } from './eduvosBlocks'

const LEVEL: Record<string, number> = { weak: 1, not_yet_full: 2, full: 3, exceptional: 4 }
const AXIS_SHORT = ['W', 'NYF', 'F', 'E']
const AXIS_LONG = ['Weak', 'Not yet full', 'Full', 'Exceptional']

/** The card gets the small one; the checklist row the readable one. */
const SIZES = {
  sm: { stepX: 30, padL: 24, r: 3.5, font: 7, yearFont: 7.5, stroke: 1.5, axis: AXIS_SHORT, height: 48 },
  md: { stepX: 54, padL: 68, r: 5, font: 9, yearFont: 9.5, stroke: 2, axis: AXIS_LONG, height: 92 },
}

const describe = (r: TalentRatingRecord) =>
  `${r.review_year} ${RATING_PERIOD_LABEL[r.review_type] || r.review_type}: ${MIDYEAR_RATING_LABEL[r.rating] || r.label || r.rating}${
    r.source === 'fo' ? ' (F&O)' : r.calibrated ? '' : ' (not calibrated)'
  }`

export default function RatingTrend({
  history,
  size = 'sm',
  height,
  className,
}: {
  history: TalentRatingRecord[]
  size?: 'sm' | 'md'
  height?: number
  className?: string
}) {
  const pts = (history || []).filter((r) => r.rating)
  if (pts.length === 0) return null
  const S = SIZES[size]
  const h = height ?? S.height
  const { stepX, padL } = S
  const padR = 10
  const width = padL + padR + Math.max(0, pts.length - 1) * stepX + 12
  const top = size === 'md' ? 10 : 6
  const bottom = h - (size === 'md' ? 18 : 14) // room for the year labels
  const rowGap = (bottom - top) / 3
  const yFor = (rating: string) => {
    const level = LEVEL[rating]
    return level ? top + (4 - level) * rowGap : bottom + 3
  }
  const xFor = (i: number) => padL + 6 + i * stepX
  const onScale = pts.map((p, i) => ({ p, i })).filter(({ p }) => !!LEVEL[p.rating])
  const line = onScale.map(({ p, i }) => `${xFor(i)},${yFor(p.rating)}`).join(' ')

  const yearLabel = (p: TalentRatingRecord) =>
    size === 'md'
      ? `${p.review_year} ${p.review_type === 'midyear' ? 'MY' : 'YE'}`
      : `${String(p.review_year).slice(2)}${p.review_type === 'midyear' ? 'M' : 'Y'}`

  return (
    <svg
      viewBox={`0 0 ${width} ${h}`}
      width={width}
      height={h}
      className={cn('block max-w-full text-heritage-blue', className)}
      role="img"
      aria-label={`Impact history: ${pts.map(describe).join('; ')}`}
      data-demo="talent-rating-trend"
    >
      {S.axis.map((label, idx) => {
        const y = top + (3 - idx) * rowGap
        return (
          <g key={label}>
            <line x1={padL} x2={width - padR} y1={y} y2={y} stroke="#e5e7eb" strokeWidth={1} />
            <text x={padL - 6} y={y + S.font / 2.5} textAnchor="end" fontSize={S.font} fill="#6b7280">
              {label}
            </text>
          </g>
        )
      })}
      {onScale.length > 1 && <polyline points={line} fill="none" stroke="currentColor" strokeWidth={S.stroke} strokeLinejoin="round" />}
      {pts.map((p, i) => (
        <g key={`${p.review_year}-${p.review_type}-${i}`}>
          <title>{describe(p)}</title>
          <circle
            cx={xFor(i)}
            cy={yFor(p.rating)}
            r={S.r}
            fill={p.source === 'fo' ? '#ffffff' : 'currentColor'}
            stroke="currentColor"
            strokeWidth={S.stroke}
            strokeDasharray={LEVEL[p.rating] ? undefined : '2 1.5'}
          />
          <text x={xFor(i)} y={h - 4} textAnchor="middle" fontSize={S.yearFont} fill="#6b7280">
            {yearLabel(p)}
          </text>
        </g>
      ))}
    </svg>
  )
}
